import { HttpErrorResponse, HttpInterceptorFn } from '@angular/common/http';
import { inject } from '@angular/core';
import { catchError, throwError } from 'rxjs';
import { MessageService } from 'primeng/api';
import type { ApiErrorResponse } from '../responses/api.response';

/**
 * Interceptor de errores HTTP
 * 
 * Maneja los errores de la API de Laravel que siguen el formato RFC 7807:
 * { type, title, status, detail, timestamp, errors? }
 * 
 * También es compatible con formatos de error tradicionales para mantener
 * retrocompatibilidad.
 */
export const errorInterceptor: HttpInterceptorFn = (req, next) => {
    const messageService = inject(MessageService);

    return next(req).pipe(
        catchError((error: HttpErrorResponse) => {
            let errorMessage = 'Ha ocurrido un error inesperado';
            let errorTitle = 'Error';

            // Verificar si el error sigue el formato RFC 7807 (Laravel API)
            const apiError = error.error as ApiErrorResponse | undefined;
            
            if (apiError && apiError.type && apiError.detail) {
                // Formato RFC 7807
                errorMessage = apiError.detail;
                errorTitle = apiError.title || 'Error';
                
                // Manejar errores de validación (422)
                if (error.status === 422 && apiError.errors) {
                    const validationErrors = Object.entries(apiError.errors)
                        .flatMap(([field, messages]) => 
                            Array.isArray(messages) 
                                ? messages.map(msg => `${field}: ${msg}`)
                                : [`${field}: ${messages}`]
                        );
                    errorMessage = validationErrors.join(', ');
                }
            } else if (error.error?.message) {
                // Formato tradicional con campo 'message'
                errorMessage = error.error.message;
            } else if (error.error?.detail) {
                // Formato alternativo con campo 'detail'
                errorMessage = error.error.detail;
            } else if (error.status === 0) {
                // Error de conexión
                errorMessage = 'Error de conexión. Verifica tu conexión a internet.';
                errorTitle = 'Error de Conexión';
            } else if (error.status === 401) {
                errorMessage = 'Sesión expirada. Por favor, inicia sesión nuevamente.';
                errorTitle = 'Sesión Expirada';
                // Redirigir al login después de un breve delay para mostrar el mensaje
                setTimeout(() => {
                    window.location.href = '/auth/login';
                }, 2000);
            } else if (error.status === 403) {
                errorMessage = 'No tienes permisos para realizar esta acción.';
                errorTitle = 'Acceso Denegado';
            } else if (error.status === 404) {
                errorMessage = 'Recurso no encontrado.';
                errorTitle = 'No Encontrado';
            } else if (error.status === 422) {
                // Errores de validación (formato tradicional si no es RFC 7807)
                if (error.error?.errors) {
                    const validationErrors = Object.values(error.error.errors).flat();
                    errorMessage = Array.isArray(validationErrors) 
                        ? validationErrors.join(', ')
                        : String(validationErrors);
                } else {
                    errorMessage = error.error?.message || 'Error de validación.';
                }
                errorTitle = 'Error de Validación';
            } else if (error.status >= 500) {
                errorMessage = 'Error del servidor. Inténtalo más tarde.';
                errorTitle = 'Error del Servidor';
            }

            // Mostrar notificación de error según el tipo usando PrimeNG
            if (error.status >= 500) {
                messageService.add({
                    severity: 'error',
                    summary: errorTitle,
                    detail: errorMessage,
                    life: 5000,
                });
            } else if (error.status === 401 || error.status === 403) {
                messageService.add({
                    severity: 'warn',
                    summary: errorTitle,
                    detail: errorMessage,
                    life: 4000,
                });
            } else if (error.status === 422) {
                // Errores de validación: mostrar como warning
                messageService.add({
                    severity: 'warn',
                    summary: errorTitle,
                    detail: errorMessage,
                    life: 5000,
                });
            } else {
                // Otros errores: mostrar como mensaje de error
                messageService.add({
                    severity: 'error',
                    summary: errorTitle,
                    detail: errorMessage,
                    life: 3000,
                });
            }

            return throwError(() => error);
        })
    );
};
